/**
 * League types and utilities for multi-league support.
 *
 * Single source of truth: add a new league by adding one entry to LEAGUE_CONFIG.
 * LeagueKey = Odds API `sport_key`, League = human-readable display name.
 *
 * Semantic helpers (usesHalves, isCollege, hasPlayerProps, etc.) replace
 * per-league checks like isNcaab(). Adding a new league = one config entry.
 */

import { easternMonthDay, easternYearMonth } from './time';

// =============================================================================
// League Properties Interface
// =============================================================================

/** BallDontLie endpoints whose availability is tracked per league. */
export const BDL_ENDPOINTS = [
  'teams',
  'games',
  'stats',
  'standings',
  'injuries',
  'plays',
  'lineups',
] as const;
export type BdlEndpoint = (typeof BDL_ENDPOINTS)[number];

export type TeamAveragesSource = 'season_averages' | 'box_scores' | null;

/** How regulation is split into periods. */
export const PERIOD_STRUCTURE = { quarters: 'quarters', halves: 'halves' } as const;
export type PeriodStructure = (typeof PERIOD_STRUCTURE)[keyof typeof PERIOD_STRUCTURE];

/** Regulation periods in a game of each structure. */
export const PERIODS_PER_GAME: Record<PeriodStructure, number> = { quarters: 4, halves: 2 };

/** stats.nba.com scoreboard ids (`LeagueID`): 00 NBA, 10 WNBA, 13 California Classic, 15 Las Vegas, 16 Salt Lake City. */
export type NbaStatsLeagueId = '00' | '10' | '13' | '15' | '16';

/** Where a league's own games' per-quarter player lines come from. */
export interface QuarterStatsSource {
  provider: 'nba_stats';
  /** Scoreboards that list the league's games, searched in this order. */
  scoreboards: readonly NbaStatsLeagueId[];
}

export interface LeagueProperties {
  label: string;
  category: 'professional' | 'college';
  periodStructure: PeriodStructure;
  quarterMinutes: number;
  hasPlayerProps: boolean;
  /** Null when no source has per-quarter player lines for this league's games. */
  quarterStatsSource: QuarterStatsSource | null;
  bdlBaseUrl: string | null;
  /** Team season averages: BDL's `team_season_averages`, our sums over its box scores, or none. */
  teamAveragesSource: TeamAveragesSource;
  /**
   * Endpoints BDL serves for this league, whether or not our plan pays for them: an unpaid one
   * answers 401 and stays a capability, so paying restores it with no code change.
   */
  bdlEndpoints: readonly BdlEndpoint[];
  espnSportPath: string | null;
  espnHasInjuries: boolean;
  /** Exact season window as MM-DD strings. Odds API may have games a few days before/after. */
  seasonDates: { start: string; end: string };
  /** Parent league key for variants (e.g., NBA preseason → 'basketball_nba'). Use string to avoid circular ref. */
  parentLeague: string | null;
  /** Odds API regions parameter: 'us' for US leagues; EuroLeague adds 'us' to 'eu,uk' so US books are captured too */
  oddsApiRegions: string;
  /** League logo URL for visual identity in selectors and headers */
  logoUrl: string;
  /** Action Network's league id, or null when AN is not shown to carry this league's props. */
  actionNetworkLeagueId: number | null;
}

// =============================================================================
// League Configuration (single source of truth)
// =============================================================================

const LEAGUE_CONFIG = {
  // === EXISTING ===
  basketball_nba: {
    label: 'NBA',
    category: 'professional',
    periodStructure: 'quarters',
    quarterMinutes: 12,
    hasPlayerProps: true,
    quarterStatsSource: { provider: 'nba_stats', scoreboards: ['00'] },
    bdlBaseUrl: 'https://api.balldontlie.io/v1',
    teamAveragesSource: 'season_averages',
    bdlEndpoints: BDL_ENDPOINTS,
    espnSportPath: 'sports/basketball/nba',
    espnHasInjuries: true,
    seasonDates: { start: '10-20', end: '06-19' }, // 2026 opening night is Oct 20 (ESPN scoreboard)
    parentLeague: null,
    oddsApiRegions: 'us',
    logoUrl: '/logos/nba.svg',
    actionNetworkLeagueId: 4,
  },
  basketball_ncaab: {
    label: 'NCAAB',
    category: 'college',
    periodStructure: 'halves',
    quarterMinutes: 20,
    hasPlayerProps: false,
    quarterStatsSource: null,
    // /cbb/v1 also answers but 404s on player_stats and team_stats; /ncaab/v1 is the documented API.
    bdlBaseUrl: 'https://api.balldontlie.io/ncaab/v1',
    teamAveragesSource: null,
    bdlEndpoints: ['teams', 'games', 'stats', 'standings', 'plays'],
    espnSportPath: 'sports/basketball/mens-college-basketball',
    espnHasInjuries: false,
    seasonDates: { start: '11-03', end: '04-06' }, // First games Nov 3 → Championship Apr 6
    parentLeague: null,
    oddsApiRegions: 'us',
    logoUrl: '/logos/ncaab.png',
    actionNetworkLeagueId: 6,
  },
  // === NEW: Core expansion ===
  basketball_wnba: {
    label: 'WNBA',
    category: 'professional',
    periodStructure: 'quarters',
    quarterMinutes: 10,
    hasPlayerProps: true,
    quarterStatsSource: { provider: 'nba_stats', scoreboards: ['10'] },
    bdlBaseUrl: 'https://api.balldontlie.io/wnba/v1',
    teamAveragesSource: 'box_scores',
    bdlEndpoints: ['teams', 'games', 'stats', 'standings', 'injuries', 'plays'],
    espnSportPath: 'sports/basketball/wnba',
    espnHasInjuries: true,
    seasonDates: { start: '05-13', end: '10-31' }, // 2026 Finals Game 7, if necessary, is Oct 31 (ESPN scoreboard)
    parentLeague: null,
    oddsApiRegions: 'us',
    logoUrl: '/logos/wnba.svg',
    actionNetworkLeagueId: 5,
  },
  basketball_wncaab: {
    label: 'WNCAAB',
    category: 'college',
    periodStructure: 'halves',
    quarterMinutes: 20,
    hasPlayerProps: false,
    quarterStatsSource: null,
    bdlBaseUrl: 'https://api.balldontlie.io/ncaaw/v1',
    teamAveragesSource: null,
    bdlEndpoints: ['teams', 'games', 'stats', 'plays'],
    espnSportPath: 'sports/basketball/womens-college-basketball',
    espnHasInjuries: false,
    seasonDates: { start: '11-03', end: '04-05' }, // First games Nov 3 → Championship Apr 5
    parentLeague: null,
    oddsApiRegions: 'us',
    logoUrl: '/logos/ncaa.png',
    actionNetworkLeagueId: 7,
  },
  basketball_euroleague: {
    label: 'EuroLeague',
    category: 'professional',
    periodStructure: 'quarters',
    quarterMinutes: 10,
    hasPlayerProps: false,
    quarterStatsSource: null,
    bdlBaseUrl: null,
    teamAveragesSource: null,
    bdlEndpoints: [],
    espnSportPath: null,
    espnHasInjuries: false,
    seasonDates: { start: '09-30', end: '05-24' }, // Regular season Sep 30 → Final Four May 24
    parentLeague: null,
    oddsApiRegions: 'us,eu,uk',
    logoUrl: '/logos/euroleague.png',
    actionNetworkLeagueId: null,
  },
  // === NEW: NBA variants (reuse NBA infrastructure) ===
  basketball_nba_preseason: {
    label: 'NBA Preseason',
    category: 'professional',
    periodStructure: 'quarters',
    quarterMinutes: 12,
    hasPlayerProps: true,
    quarterStatsSource: { provider: 'nba_stats', scoreboards: ['00'] },
    bdlBaseUrl: 'https://api.balldontlie.io/v1',
    teamAveragesSource: 'season_averages',
    bdlEndpoints: BDL_ENDPOINTS,
    espnSportPath: 'sports/basketball/nba',
    espnHasInjuries: true,
    seasonDates: { start: '10-02', end: '10-17' }, // ~Oct 2–17
    parentLeague: 'basketball_nba',
    oddsApiRegions: 'us',
    logoUrl: '/logos/nba.svg',
    actionNetworkLeagueId: 4,
  },
  basketball_nba_summer_league: {
    label: 'NBA Summer League',
    category: 'professional',
    periodStructure: 'quarters',
    quarterMinutes: 10,
    hasPlayerProps: true,
    // Las Vegas, then the California Classic and Salt Lake City; each runs its own scoreboard.
    quarterStatsSource: { provider: 'nba_stats', scoreboards: ['15', '13', '16'] },
    bdlBaseUrl: 'https://api.balldontlie.io/v1',
    teamAveragesSource: 'season_averages',
    bdlEndpoints: ['teams'], // BDL carries no Summer League games
    // ESPN runs Summer League as its own league: for 2026-07-12, `sports/basketball/nba`
    // returns 0 events and this path returns 8. BDL carries no Summer League at all.
    espnSportPath: 'sports/basketball/nba-summer-las-vegas',
    espnHasInjuries: false,
    seasonDates: { start: '07-05', end: '07-20' }, // California Classic Jul 5 → Vegas Jul 20
    parentLeague: 'basketball_nba',
    oddsApiRegions: 'us',
    logoUrl: '/logos/nba.svg',
    actionNetworkLeagueId: null,
  },
  basketball_nba_all_stars: {
    label: 'NBA All-Stars',
    category: 'professional',
    periodStructure: 'quarters',
    quarterMinutes: 12,
    hasPlayerProps: true,
    // Each 2026 game was one 12-minute period, so its first-quarter box held all of regulation.
    quarterStatsSource: null,
    bdlBaseUrl: 'https://api.balldontlie.io/v1',
    teamAveragesSource: 'season_averages',
    bdlEndpoints: BDL_ENDPOINTS,
    espnSportPath: 'sports/basketball/nba',
    espnHasInjuries: false,
    seasonDates: { start: '02-14', end: '02-16' }, // All-Star Weekend Feb 14–16
    parentLeague: 'basketball_nba',
    oddsApiRegions: 'us',
    logoUrl: '/logos/nba.svg',
    actionNetworkLeagueId: null,
  },
} as const satisfies Record<string, LeagueProperties>;

// =============================================================================
// Core Types (derived from config)
// =============================================================================

/** Odds API sport_key values */
export type LeagueKey = keyof typeof LEAGUE_CONFIG;

/** Human-readable league names */
export type League = (typeof LEAGUE_CONFIG)[LeagueKey]['label'];

/** All supported league keys */
export const SUPPORTED_LEAGUE_KEYS: readonly LeagueKey[] =
  Object.keys(LEAGUE_CONFIG).filter(isLeagueKey);

/** All supported display-name leagues (for UI toggles) */
export const SUPPORTED_LEAGUES: readonly League[] = SUPPORTED_LEAGUE_KEYS.map(
  (k) => LEAGUE_CONFIG[k].label,
) as League[];

/** Labels of the supported leagues that satisfy a capability, e.g. a chat tool's league enum. */
export function leagueLabelsWhere(predicate: (key: LeagueKey) => boolean): League[] {
  return SUPPORTED_LEAGUE_KEYS.filter(predicate).map((k) => LEAGUE_CONFIG[k].label);
}

export const DEFAULT_LEAGUE: League = 'NBA';
/**
 * Only for a surface with no league of its own (the out-of-season fallback in getDefaultLeagueKey,
 * a connectivity probe). Never a stand-in for a request's, row's or user's missing league.
 */
export const DEFAULT_LEAGUE_KEY: LeagueKey = 'basketball_nba';

// Derived reverse maps for lookups
const KEY_TO_LEAGUE = Object.fromEntries(
  SUPPORTED_LEAGUE_KEYS.map((k) => [k, LEAGUE_CONFIG[k].label]),
) as Record<string, League>;

const LEAGUE_TO_KEY = Object.fromEntries(
  SUPPORTED_LEAGUE_KEYS.map((k) => [LEAGUE_CONFIG[k].label, k]),
) as Record<string, LeagueKey>;

// =============================================================================
// Config Accessor
// =============================================================================

/** Get the full config for a league */
export function getLeagueConfig(key: LeagueKey): LeagueProperties {
  return LEAGUE_CONFIG[key];
}

// =============================================================================
// Semantic Helpers (replace isNcaab() antipattern)
// =============================================================================

/** Does this league play halves? (NCAAB, WNCAAB) */
export function usesHalves(key: LeagueKey): boolean {
  return LEAGUE_CONFIG[key].periodStructure === 'halves';
}

/** Does this league play quarters? (NBA, WNBA, EuroLeague, NBA variants) */
export function usesQuarters(key: LeagueKey): boolean {
  return LEAGUE_CONFIG[key].periodStructure === 'quarters';
}

/** Is this a college league? (NCAAB, WNCAAB) */
export function isCollege(key: LeagueKey): boolean {
  return LEAGUE_CONFIG[key].category === 'college';
}

/** Is this a professional league? (NBA, WNBA, EuroLeague, NBA variants) */
export function isProfessional(key: LeagueKey): boolean {
  return LEAGUE_CONFIG[key].category === 'professional';
}

/** Does this league have player props available on Odds API? */
export function hasPlayerProps(key: LeagueKey): boolean {
  return LEAGUE_CONFIG[key].hasPlayerProps;
}

/** Where this league's own games' per-quarter player lines come from, or null. */
export function getQuarterStatsSource(key: LeagueKey): QuarterStatsSource | null {
  return LEAGUE_CONFIG[key].quarterStatsSource;
}

export function hasQuarterStatsSource(key: LeagueKey): boolean {
  return getQuarterStatsSource(key) !== null;
}

/** The source for a player's recent games: BallDontLie reads them from the parent league's API. */
export function getRecentGamesQuarterStatsSource(key: LeagueKey): QuarterStatsSource | null {
  return getQuarterStatsSource(getParentLeague(key));
}

/** Get the human-readable label for a league */
export function getTeamAveragesSource(key: LeagueKey): TeamAveragesSource {
  return LEAGUE_CONFIG[key].teamAveragesSource;
}

export function getLeagueLabel(key: LeagueKey): string {
  return LEAGUE_CONFIG[key].label;
}

/** Get the BDL base URL for a league (null if no BDL coverage) */
export function getBdlBaseUrl(key: LeagueKey): string | null {
  return LEAGUE_CONFIG[key].bdlBaseUrl;
}

/**
 * BDL stats endpoint name keyed by league. NBA uses `/stats`; WNBA and the college
 * APIs use `/player_stats`. Per CLAUDE.md §League-Aware Logic, this is a
 * Partial<Record<LeagueKey, T>> lookup so new leagues add a single entry
 * instead of growing if/else chains at the call sites.
 */
const BDL_STATS_ENDPOINT_BY_LEAGUE: Partial<Record<LeagueKey, 'player_stats' | 'stats'>> = {
  basketball_wnba: 'player_stats',
  basketball_ncaab: 'player_stats',
  basketball_wncaab: 'player_stats',
};

/** Get the BDL stats-endpoint segment for a league (`/stats` or `/player_stats`). */
export function getBdlStatsEndpoint(key: LeagueKey): 'player_stats' | 'stats' {
  return BDL_STATS_ENDPOINT_BY_LEAGUE[key] ?? 'stats';
}

/**
 * Get the "season year" the Eastern date of `date` falls in for the given league.
 *
 * - Single-calendar-year seasons (start month ≤ end month, e.g. WNBA Apr-Oct):
 *   season year = calendar year. May 2026 → 2026.
 * - Wrap-around seasons (start > end, e.g. NBA Oct-Jun, NCAAB Nov-Apr,
 *   Euroleague Sep-May): season year = the year the season started.
 *   May 2026 → 2025 (still part of "2025-26" season). Oct 2025 → 2025.
 *
 * Variants (NBA preseason / summer-league / all-stars) defer to their
 * parentLeague's season math so they share a season year with the parent
 * — preseason in Oct 2025 is part of the NBA 2025-26 season (year 2025),
 * not a standalone calendar-year 2025 event.
 *
 * Replaces the ternary `leagueKey === 'basketball_wnba' ? d.getFullYear() :
 * d.getFullYear() - (d.getMonth() < 9 ? 1 : 0)`, which used the NBA Oct
 * cutover for every non-WNBA league — silently wrong for any league with a
 * different season start (Euroleague Sep, NCAAB Nov).
 */
export function getSeasonYear(date: Date, key: LeagueKey): number {
  const effective = getParentLeague(key);
  const config = LEAGUE_CONFIG[effective];
  const startMonth = Number(config.seasonDates.start.split('-')[0]);
  const endMonth = Number(config.seasonDates.end.split('-')[0]);
  const { year: calendarYear, month: currentMonth } = easternYearMonth(date);

  // Season fits within one calendar year (start month ≤ end month).
  if (startMonth <= endMonth) return calendarYear;

  // Wrap-around season: pre-cutover dates belong to the previous season year.
  return currentMonth >= startMonth ? calendarYear : calendarYear - 1;
}

/** "2025-26" for a season that spans two calendar years, "2026" for one that does not (WNBA). */
export function formatSeasonLabel(startYear: number, key: LeagueKey): string {
  const { start, end } = getSeasonDates(getParentLeague(key));
  if (mmddToNumber(start) <= mmddToNumber(end)) return String(startYear);
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
}

/** Get the ESPN sport path for a league (null if no ESPN coverage) */
export function getEspnSportPath(key: LeagueKey): string | null {
  return LEAGUE_CONFIG[key].espnSportPath;
}

/** Does this league have ESPN injury data? */
export function hasEspnInjuries(key: LeagueKey): boolean {
  return LEAGUE_CONFIG[key].espnHasInjuries;
}

/** Is this a player-prop league whose injuries no source reports (BallDontLie serves none, ESPN has none)? */
export function isPropLeagueWithoutInjuryReports(key: LeagueKey): boolean {
  return hasPlayerProps(key) && !hasBdlEndpoint(key, 'injuries') && !hasEspnInjuries(key);
}

/** Regulation game length in minutes: 48 in the NBA, 40 in the WNBA, EuroLeague and college. */
export function regulationMinutes(key: LeagueKey): number {
  const config = LEAGUE_CONFIG[key];
  return PERIODS_PER_GAME[config.periodStructure] * config.quarterMinutes;
}

/** Get the parent league key for NBA variants, or the key itself */
export function getParentLeague(key: LeagueKey): LeagueKey {
  return LEAGUE_CONFIG[key].parentLeague ?? key;
}

/** Is this an NBA variant (preseason, summer league, all-stars)? */
export function isNbaVariant(key: LeagueKey): boolean {
  return LEAGUE_CONFIG[key].parentLeague === 'basketball_nba';
}

/** Does this league have BDL coverage? */
export function hasBdlCoverage(key: LeagueKey): boolean {
  return LEAGUE_CONFIG[key].bdlBaseUrl !== null;
}

/** Does BDL serve this endpoint for this league (paid or not)? */
export function hasBdlEndpoint(key: LeagueKey, endpoint: BdlEndpoint): boolean {
  const endpoints: readonly BdlEndpoint[] = LEAGUE_CONFIG[key].bdlEndpoints;
  return endpoints.includes(endpoint);
}

/** Does this league have ESPN coverage? */
export function hasEspnCoverage(key: LeagueKey): boolean {
  return LEAGUE_CONFIG[key].espnSportPath !== null;
}

/** Does this league have any stats coverage (BDL or EuroLeague API)? */
export function hasStatsCoverage(key: LeagueKey): boolean {
  return hasBdlCoverage(key) || key === 'basketball_euroleague';
}

/** Action Network's league id for this league, or null when AN is not shown to cover it. */
export function getActionNetworkLeagueId(key: LeagueKey): number | null {
  return LEAGUE_CONFIG[key].actionNetworkLeagueId;
}

/** Each Action Network league we read, with every league key that maps to it, by AN id. */
export function getActionNetworkLeagues(): Array<{ leagueId: number; leagueKeys: LeagueKey[] }> {
  const byId = new Map<number, LeagueKey[]>();
  for (const key of SUPPORTED_LEAGUE_KEYS) {
    const id = getActionNetworkLeagueId(key);
    if (id != null) byId.set(id, [...(byId.get(id) ?? []), key]);
  }
  return [...byId.entries()]
    .sort(([a], [b]) => a - b)
    .map(([leagueId, leagueKeys]) => ({ leagueId, leagueKeys }));
}

/** Get the Odds API regions parameter for a league ('us' or 'us,eu,uk') */
export function getOddsApiRegions(key: LeagueKey): string {
  return LEAGUE_CONFIG[key].oddsApiRegions;
}

/** Whether a league's Odds API requests cover the us region, so US books are fetched and accepted. */
export function oddsApiRegionsIncludeUs(key: LeagueKey): boolean {
  return getOddsApiRegions(key).split(',').includes('us');
}

/** Get the logo URL for a league (for selectors and headers) */
export function getLeagueLogoUrl(key: LeagueKey): string {
  return LEAGUE_CONFIG[key].logoUrl;
}

/** Get the season date boundaries (MM-DD strings) for a league */
export function getSeasonDates(key: LeagueKey): { start: string; end: string } {
  return LEAGUE_CONFIG[key].seasonDates;
}

/**
 * Parse a MM-DD season boundary into a comparable number (month * 100 + day).
 * e.g., '10-21' → 1021, '06-19' → 619
 */
function mmddToNumber(mmdd: string): number {
  const [m, d] = mmdd.split('-').map(Number);
  return m * 100 + d;
}

/**
 * Check if a league is in season on the Eastern date of `date`, so the server and phones agree.
 * Uses MM-DD boundaries from seasonDates config.
 * Handles wrap-around seasons (e.g., NBA: 10-20 → 06-19 spans year boundary).
 */
export function isInSeason(key: LeagueKey, date: Date = new Date()): boolean {
  const { start, end } = LEAGUE_CONFIG[key].seasonDates;
  const startNum = mmddToNumber(start);
  const endNum = mmddToNumber(end);
  const todayNum = mmddToNumber(easternMonthDay(date));

  if (startNum <= endNum) {
    // Season within same year (e.g., WNBA: 05-13 → 10-31)
    return todayNum >= startNum && todayNum <= endNum;
  }
  // Season wraps around year boundary (e.g., NBA: 10-20 → 06-19)
  return todayNum >= startNum || todayNum <= endNum;
}

/** Is MM-DD `monthDay` in the NBA-family preseason stats window: the opener's month 1st through opening day. */
export function isInPreseasonStatsWindow(key: LeagueKey, monthDay: string): boolean {
  if (getParentLeague(key) !== 'basketball_nba') return false;
  const start = getSeasonDates('basketball_nba').start;
  const day = mmddToNumber(monthDay);
  return day >= mmddToNumber(`${start.slice(0, 2)}-01`) && day <= mmddToNumber(start);
}

/** Can a player lookup in this league return stats on `date`: in season, or in the preseason window. */
export function hasPlayerStatsOn(key: LeagueKey, date: Date = new Date()): boolean {
  return isInSeason(key, date) || isInPreseasonStatsWindow(key, easternMonthDay(date));
}

/**
 * Get only the leagues that are currently in season.
 * Returns LeagueKeys that have active games right now.
 */
export function getInSeasonLeagueKeys(date: Date = new Date()): LeagueKey[] {
  return SUPPORTED_LEAGUE_KEYS.filter((key) => isInSeason(key, date));
}

/**
 * Get only the league display names that are currently in season.
 */
export function getInSeasonLeagues(date: Date = new Date()): League[] {
  return getInSeasonLeagueKeys(date).map((k) => LEAGUE_CONFIG[k].label) as League[];
}

/**
 * The leagues a connectivity probe should hit: every in-season league, or DEFAULT_LEAGUE_KEY alone
 * when none is in season and any league proves the connection.
 */
export function getProbeLeagueKeys(date: Date = new Date()): LeagueKey[] {
  const inSeason = getInSeasonLeagueKeys(date);
  return inSeason.length > 0 ? inSeason : [DEFAULT_LEAGUE_KEY];
}

/**
 * Resolve the league a user with no saved preference should land on.
 * Prefers the first in-season league (by SUPPORTED_LEAGUE_KEYS priority, so
 * flagship leagues win over variants) — otherwise a new user opens onto an
 * empty off-season slate (e.g. NBA in July). Falls back to DEFAULT_LEAGUE_KEY
 * when nothing is in season. A saved `preferredLeague` always wins at the call
 * site; this only supplies the default when none is set.
 */
export function getDefaultLeagueKey(date: Date = new Date()): LeagueKey {
  return getInSeasonLeagueKeys(date)[0] ?? DEFAULT_LEAGUE_KEY;
}

// =============================================================================
// Backwards Compat (keep isNcaab for existing call sites during migration)
// =============================================================================

/** @deprecated Use semantic helpers (isCollege, usesHalves, hasPlayerProps) instead */
export function isNcaab(leagueKey: LeagueKey): boolean {
  return leagueKey === 'basketball_ncaab';
}

// =============================================================================
// Lookup Functions
// =============================================================================

export function leagueToLeagueKey(league: League): LeagueKey {
  return LEAGUE_TO_KEY[league];
}

export function leagueKeyToLeague(leagueKey: LeagueKey): League {
  return KEY_TO_LEAGUE[leagueKey];
}

// Object.hasOwn, not `in`: `in` also finds names every object inherits, such as 'constructor'.
export function isLeague(value: string): value is League {
  return Object.hasOwn(LEAGUE_TO_KEY, value);
}

// Reads LEAGUE_CONFIG, not KEY_TO_LEAGUE: SUPPORTED_LEAGUE_KEYS, which KEY_TO_LEAGUE is built from, filters with it.
export function isLeagueKey(value: string): value is LeagueKey {
  return Object.hasOwn(LEAGUE_CONFIG, value);
}

/** The value as a LeagueKey, or null when it names no league. For wire or stored data typed loosely. */
export function leagueKeyOrNull(value: unknown): LeagueKey | null {
  return typeof value === 'string' && isLeagueKey(value) ? value : null;
}

/**
 * The league a key ('basketball_wnba') or display name ('WNBA') names, or null when the value
 * names no league. Callers decide what "no league" means; there is no default.
 */
export function parseLeagueKey(value: unknown): LeagueKey | null {
  const key = leagueKeyOrNull(value);
  if (key) return key;
  return typeof value === 'string' && isLeague(value) ? leagueToLeagueKey(value) : null;
}

/**
 * The league a saved preference names (a key or a display name), or the in-season default
 * when it is missing or names no league.
 */
export function preferredLeagueKeyOrDefault(
  preferred: string | null | undefined,
  date: Date = new Date(),
): LeagueKey {
  return parseLeagueKey(preferred) ?? getDefaultLeagueKey(date);
}

/**
 * The saved league if it is in season on `date`, else the in-season default (the prop browser).
 */
export function preferredInSeasonLeagueKey(
  preferred: string | null | undefined,
  date: Date = new Date(),
): LeagueKey {
  const preferredKey = parseLeagueKey(preferred);
  return preferredKey && isInSeason(preferredKey, date) ? preferredKey : getDefaultLeagueKey(date);
}

// =============================================================================
// Game & Parlay Helpers
// =============================================================================

/**
 * Filter games by league using the sport_key field from Odds API.
 * If no league is specified, returns all games.
 */
export function filterGamesByLeague<T extends { sport_key?: string }>(
  games: T[],
  league: League | null,
): T[] {
  if (!league) return games;
  const key = leagueToLeagueKey(league);
  return games.filter((g) => g.sport_key === key);
}

/**
 * Games whose sport_key names a league, typed as one; each other game goes to `onUnknownLeague`.
 */
export function gamesWithKnownLeague<T extends { sport_key?: unknown }>(
  games: readonly T[],
  onUnknownLeague: (game: T) => void,
): Array<T & { sport_key: LeagueKey }> {
  const known: Array<T & { sport_key: LeagueKey }> = [];
  for (const game of games) {
    const leagueKey = leagueKeyOrNull(game.sport_key);
    if (leagueKey) known.push({ ...game, sport_key: leagueKey });
    else onUnknownLeague(game);
  }
  return known;
}

/**
 * Derive a parlay's league from its legs' leagueKeys.
 * Single league -> that key. Multiple -> comma-separated sorted keys. No legs -> null.
 */
export function deriveParlayLeague(leagueKeys: string[]): string | null {
  const unique = [...new Set(leagueKeys)].sort();
  return unique.length === 0 ? null : unique.join(',');
}

/**
 * Check if a parlay's league value includes a specific league key.
 * Works for both single-league ('basketball_nba') and multi-league ('basketball_nba,basketball_ncaab').
 */
export function parlayMatchesLeague(parlayLeague: string, leagueKey: string): boolean {
  return parlayLeague.split(',').includes(leagueKey);
}
